// domain/constants/importableFields.js
import { PROGRAM_STATUS } from './programStatus';
import { TARGET_DATA_TYPE } from './targetDataType';
import { TARGET_STATUS } from './targetStatus';

/**
 * Which fields of a program and a target can be brought in from outside, and what each one is.
 *
 * DECLARED HERE BECAUSE THEY ARE OWNED HERE. This list used to live in the importer's constants,
 * which meant Treatment Planning's field list, its length limits and its enum vocabularies were all
 * decided in a context that has no business knowing them. Six of the seven status defects that
 * prompted this extraction came from exactly that. Onboarding translates this into whatever its pipeline needs; see
 * `ImportTarget.port`.
 *
 * IT SAYS NOTHING ABOUT FILES. No column headers, no spellings, no formats. A file's vocabulary is
 * the importer's anti-corruption layer and lives there, so that changing how a spreadsheet spells
 * `aborted` cannot reach into this context.
 *
 * THIS IS NOT THE WHOLE MODEL, deliberately. A program has around sixty-eight stored fields and a
 * target forty-five; these are the ones an import can set. Adding one here is what makes it
 * importable, which is the point: the decision is a clinical one, made here.
 *
 * @typedef {import('src/contexts/onboarding').FieldDeclaration} FieldDeclaration
 */

/** Stored value → i18n key. The key is the label; the spelling in a FILE is onboarding's business. */
export const PROGRAM_STATUS_VOCABULARY = {
  [PROGRAM_STATUS.WAITING]: 'programStatus_waiting',
  [PROGRAM_STATUS.BASELINE]: 'programStatus_baseline',
  [PROGRAM_STATUS.ACTIVE]: 'programStatus_active',
  [PROGRAM_STATUS.MAINTENANCE]: 'programStatus_maintenance',
  [PROGRAM_STATUS.HOLD]: 'programStatus_hold',
  [PROGRAM_STATUS.CLOSED]: 'programStatus_closed',
  [PROGRAM_STATUS.DISCONTINUED]: 'programStatus_discontinued',
};

export const TARGET_STATUS_VOCABULARY = {
  [TARGET_STATUS.WAITING]: 'targetStatus_waiting',
  [TARGET_STATUS.PROBE]: 'targetStatus_probe',
  [TARGET_STATUS.ACQUISITION]: 'targetStatus_acquisition',
  [TARGET_STATUS.MASTERED]: 'targetStatus_mastered',
  [TARGET_STATUS.CLOSED]: 'targetStatus_closed',
  [TARGET_STATUS.HOLD]: 'targetStatus_hold',
  [TARGET_STATUS.ABORTED]: 'targetStatus_aborted',
};

export const TARGET_DATA_TYPE_VOCABULARY = {
  [TARGET_DATA_TYPE.DISCRETE_TRIAL]: 'programs.targets.dataTypes.discreteTrial',
  [TARGET_DATA_TYPE.TARGET_SET]: 'programs.targets.dataTypes.targetSet',
  [TARGET_DATA_TYPE.TASK_ANALYSIS]: 'programs.targets.dataTypes.taskAnalysis',
  [TARGET_DATA_TYPE.DURATION]: 'programs.targets.dataTypes.duration',
  [TARGET_DATA_TYPE.RATE]: 'programs.targets.dataTypes.rate',
  [TARGET_DATA_TYPE.FREQUENCY]: 'programs.targets.dataTypes.frequency',
  [TARGET_DATA_TYPE.SHAPING]: 'programs.targets.dataTypes.shaping',
  [TARGET_DATA_TYPE.RANDOM_ROTATION]: 'programs.targets.dataTypes.randomRotation',
  [TARGET_DATA_TYPE.CAB_BRANCH]: 'programs.targets.dataTypes.cabBranch',
  [TARGET_DATA_TYPE.INSTRUCTIONS]: 'programs.targets.dataTypes.instructions',
  [TARGET_DATA_TYPE.INTERVAL]: 'programs.targets.dataTypes.interval',
};

/**
 * Which program type a file may state.
 *
 * TWO OF ELEVEN, and the nine are a product decision rather than an omission. The data collection
 * method is becoming a fact about a target rather than a program: interval programs have already
 * been ported to interval targets, and duration, frequency and task analysis are following. Nothing
 * should arrive through an import in a vocabulary we are leaving.
 *
 * Skill Acquisition is the one an import creates by default. Instructions Only joins it because it
 * carries no targets at all, so nothing about it depends on the method question above. Every data
 * type lists the program types that accept it and none of them lists this one, which is what makes
 * a target inside an instructions program refuse itself without a rule of its own.
 *
 * A FILE MAY SAY NOTHING ELSE. The retired spellings were briefly folded into Skill Acquisition so
 * that older templates kept working, and that was the wrong trade: a file saying
 * `Program Type: Duration` describes a program the import will not create, and quietly making it a
 * Skill Acquisition one hands the author something they did not ask for. Those files are refused
 * now and their author edits one cell.
 *
 * A BLANK CELL, OR NO SUCH COLUMN, STILL MEANS SKILL ACQUISITION. Saying nothing is not the same as
 * naming a type we cannot honour, and most files say nothing.
 */
export const PROGRAM_TYPE_VOCABULARY = {
  simple: 'programType_simple',
  instructions: 'programType_instructions',
};

/**
 * The measurement metric a program's graphs are drawn against.
 *
 * `ratePerDay` is absent because it is not a metric the app has — `measurementMetrics` holds eight
 * ids and that is not one, so importing it set a metric nothing could ever resolve.
 */
export const PRIMARY_METRIC_VOCABULARY = {
  percentCorrect: 'measurementMetrics.percentCorrect.label',
  frequency: 'measurementMetrics.frequency.label',
  totalDuration: 'measurementMetrics.totalDuration.label',
  minDuration: 'measurementMetrics.minDuration.label',
  maxDuration: 'measurementMetrics.maxDuration.label',
  avgDuration: 'measurementMetrics.avgDuration.label',
  ratePerMinute: 'measurementMetrics.ratePerMinute.label',
  ratePerHour: 'measurementMetrics.ratePerHour.label',
};

/** The maintenance intervals the app offers, in days. */
export const MAINTENANCE_INTERVAL_DAYS = [1, 7, 14, 30, 91, 183, 365];

/** @type {Object.<string, Object.<string, FieldDeclaration>>} */
export const PROGRAM_IMPORTABLE_FIELDS = {
  program: {
    programName: {
      labelKey: 'importPipeline.fields.program.programName',
      kind: 'text',
      required: true,
      minLength: 1,
      maxLength: 255,
    },
    type: {
      labelKey: 'importPipeline.fields.program.type',
      kind: 'enum',
      vocabulary: PROGRAM_TYPE_VOCABULARY,
    },
    objective: {
      labelKey: 'importPipeline.fields.program.objective',
      kind: 'text',
      maxLength: 1000,
    },
    instructions: {
      labelKey: 'importPipeline.fields.program.instructions',
      kind: 'text',
    },
    tags: {
      labelKey: 'importPipeline.fields.program.tags',
      kind: 'enumSet',
    },
    // Not `required`, though every program needs one: a file that names none gets the clinic's
    // default, so only a clinic without a default leaves it missing. The import target says so.
    promptLevelTemplate: {
      labelKey: 'importPipeline.fields.program.promptLevelTemplate',
      kind: 'reference',
      referenceType: 'promptLevelTemplate',
    },
    workflow: {
      labelKey: 'importPipeline.fields.program.workflow',
      kind: 'reference',
      referenceType: 'workflow',
    },
    treatmentArea: {
      labelKey: 'importPipeline.fields.program.treatmentArea',
      kind: 'reference',
      referenceType: 'treatmentArea',
    },
    status: {
      labelKey: 'importPipeline.fields.program.status',
      kind: 'enum',
      vocabulary: PROGRAM_STATUS_VOCABULARY,
    },
    primaryMetric: {
      labelKey: 'importPipeline.fields.program.primaryMetric',
      kind: 'enum',
      vocabulary: PRIMARY_METRIC_VOCABULARY,
    },
    fillZeroDuration: {
      labelKey: 'importPipeline.fields.program.fillZeroDuration',
      kind: 'boolean',
    },
  },

  programTarget: {
    label: {
      labelKey: 'importPipeline.fields.programTarget.label',
      kind: 'text',
      required: true,
      minLength: 1,
      maxLength: 255,
    },
    discriminativeStimulus: {
      labelKey: 'importPipeline.fields.programTarget.discriminativeStimulus',
      kind: 'text',
      maxLength: 100,
    },
    status: {
      labelKey: 'importPipeline.fields.programTarget.status',
      kind: 'enum',
      vocabulary: TARGET_STATUS_VOCABULARY,
    },
    dataType: {
      labelKey: 'importPipeline.fields.programTarget.dataType',
      kind: 'enum',
      vocabulary: TARGET_DATA_TYPE_VOCABULARY,
    },
    interval: {
      labelKey: 'importPipeline.fields.programTarget.interval',
      kind: 'number',
      min: 1,
    },
    // A TARGET MAY OVERRIDE ITS PROGRAM'S WORKFLOW AND TEMPLATE, and only some target types have
    // either: `hasWorkflow` is discrete trial, duration, rate and shaping, and every type but
    // instructions has a prompt level template. A row naming one its type does not have is refused
    // rather than written somewhere nothing reads it.
    workflow: {
      labelKey: 'importPipeline.fields.programTarget.workflow',
      kind: 'reference',
      referenceType: 'workflow',
    },
    promptLevelTemplate: {
      labelKey: 'importPipeline.fields.programTarget.promptLevelTemplate',
      kind: 'reference',
      referenceType: 'promptLevelTemplate',
    },
    // HOW OFTEN A MASTERED TARGET IS PROBED, in days, and not any number of them. The app offers
    // seven, and refusing anything else is deliberate: 45 days is a decision somebody typed, and
    // rounding it to 30 changes when a clinician sees the target again.
    maintenanceInterval: {
      labelKey: 'importPipeline.fields.programTarget.maintenanceInterval',
      kind: 'number',
      oneOf: MAINTENANCE_INTERVAL_DAYS,
    },
    // The instruction text an instructions target carries, and the only field that type has of its
    // own. The type was already importable, which made it selectable and not much else.
    instructions: {
      labelKey: 'importPipeline.fields.programTarget.instructions',
      kind: 'text',
    },
    // A TARGET MAY OVERRIDE ITS PROGRAM'S METRIC, and a file that mixes target types in one
    // program has to. A source describing a counted behaviour and a timed one under the same
    // program leaves the program no single honest metric, and `deriveProgramDefaults` then writes
    // each target its own — this is what lets an adapter state one the data type does not imply.
    primaryMetric: {
      labelKey: 'importPipeline.fields.programTarget.primaryMetric',
      kind: 'enum',
      vocabulary: PRIMARY_METRIC_VOCABULARY,
    },
    introductionDate: { labelKey: 'importPipeline.fields.programTarget.introductionDate', kind: 'date' },
    masteryDate: { labelKey: 'importPipeline.fields.programTarget.masteryDate', kind: 'date' },
    holdDate: { labelKey: 'importPipeline.fields.programTarget.holdDate', kind: 'date' },
    abortedDate: { labelKey: 'importPipeline.fields.programTarget.abortedDate', kind: 'date' },
  },
};
